import React from 'react';
import { Button } from '../ui/button';
import { Trophy, ChevronRight, Flame, Zap, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LEVEL_STYLES = {
  1: { border: 'border-zinc-500/40', glow: 'shadow-[0_0_40px_rgba(161,161,170,0.15)]', accent: 'text-zinc-300' },
  2: { border: 'border-blue-500/40', glow: 'shadow-[0_0_40px_rgba(59,130,246,0.2)]', accent: 'text-blue-400' },
  3: { border: 'border-primary/40', glow: 'shadow-[0_0_60px_rgba(212,255,0,0.2)]', accent: 'text-primary' },
  4: { border: 'border-orange-500/40', glow: 'shadow-[0_0_60px_rgba(249,115,22,0.25)]', accent: 'text-orange-400' },
  5: { border: 'border-amber-400/50', glow: 'shadow-[0_0_80px_rgba(251,191,36,0.3)]', accent: 'text-amber-400' },
};

export default function WorkoutCompletionModal({ data, language, onClose }) {
  const navigate = useNavigate();
  const fr = language === 'fr';

  if (!data) return null;

  const dopamine = data.dopamine || {};
  const challenge = data.challenge || {};
  const level = dopamine.level || 1;
  const style = LEVEL_STYLES[level] || LEVEL_STYLES[1];

  const handleContinue = () => {
    onClose();
    navigate('/dashboard');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="workout-completion-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      <div
        className={`relative w-full max-w-md bg-card rounded-2xl overflow-hidden ${style.border} border-2 ${style.glow} animate-in zoom-in-95 fade-in duration-500`}
        data-testid="dopamine-card"
      >
        {/* Image */}
        <div className="relative h-56 sm:h-64 overflow-hidden">
          <img
            src={dopamine.image}
            alt={dopamine.title}
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />

          {/* Level badge */}
          <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/60 backdrop-blur-md rounded-full px-3 py-1.5 border border-white/10">
            <Flame className={`w-4 h-4 ${style.accent}`} />
            <span className={`text-xs font-bold uppercase tracking-wider ${style.accent}`}>
              {data.weekly_completed}/sem
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6 -mt-8 relative space-y-4">
          {/* Title */}
          <div className="text-center">
            <h2
              className={`font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight ${style.accent}`}
              data-testid="dopamine-title"
            >
              {dopamine.title}
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
              {dopamine.message}
            </p>
          </div>

          {/* Stats row */}
          <div className="flex items-center justify-center gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <Zap className="w-4 h-4 text-primary" />
                <span className="font-heading text-xl font-black text-foreground">+{data.calories_bonus || 0}</span>
              </div>
              <span className="text-[10px] uppercase text-muted-foreground tracking-wider">cal bonus</span>
            </div>
            {challenge.validated && (
              <div className="text-center" data-testid="challenge-points">
                <div className="flex items-center justify-center gap-1">
                  <Award className="w-4 h-4 text-amber-400" />
                  <span className="font-heading text-xl font-black text-amber-400">+{challenge.points}</span>
                </div>
                <span className="text-[10px] uppercase text-muted-foreground tracking-wider">
                  {fr ? 'pts défi' : 'challenge pts'}
                </span>
              </div>
            )}
            {data.new_badges && data.new_badges.length > 0 && (
              <div className="text-center" data-testid="new-badges">
                <div className="flex items-center justify-center gap-1">
                  <Trophy className="w-4 h-4 text-primary" />
                  <span className="font-heading text-xl font-black text-primary">{data.new_badges.length}</span>
                </div>
                <span className="text-[10px] uppercase text-muted-foreground tracking-wider">
                  {fr ? 'nouveau badge' : 'new badge'}
                </span>
              </div>
            )}
          </div>

          {/* Challenge info */}
          {challenge.validated && challenge.info && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-center">
              <p className="text-xs text-amber-300 font-medium">
                {challenge.info.title_fr} — {fr ? 'Jour' : 'Day'} {challenge.info.day} {fr ? 'valide' : 'validated'} !
              </p>
            </div>
          )}

          {/* CTA */}
          <Button
            onClick={handleContinue}
            className="w-full h-12 font-heading text-base uppercase tracking-wider"
            data-testid="continue-to-dashboard-btn"
          >
            {fr ? 'Continuer vers le tableau de bord' : 'Continue to dashboard'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
